import React, { useState, useEffect, useRef } from 'react';
import { designerService, Designer, CreateDesignerRequest, UpdateDesignerRequest } from '../services/designer';
import { useToast } from '../contexts/ToastContext';
import ImageCropModal from './ImageCropModal';

interface DesignerModalProps {
  designer: Designer | null;
  onClose: () => void;
  onSave: () => void;
}

const DesignerModal: React.FC<DesignerModalProps> = ({ designer, onClose, onSave }) => {
  const [formData, setFormData] = useState<CreateDesignerRequest | UpdateDesignerRequest>({
    name: '',
    description: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [bannerPreview, setBannerPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  
  const [cropTarget, setCropTarget] = useState<'logo' | 'banner' | null>(null);
  const [rawCropSrc, setRawCropSrc] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (designer) {
      setFormData({
        name: designer.name,
        description: designer.description || '',
        isActive: designer.isActive,
      });
      
      // Set image previews if designer has images
      if (designer.logoUrl) {
        setLogoPreview(designer.logoUrl);
      } else if (designer.logoPath) {
        setLogoPreview(designerService.getImageUrl(designer.logoPath) || '');
      }
      if (designer.bannerUrl) {
        setBannerPreview(designer.bannerUrl);
      } else if (designer.bannerPath) {
        setBannerPreview(designerService.getImageUrl(designer.bannerPath) || '');
      }
    }
  }, [designer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openCropForFile = (file: File, target: 'logo' | 'banner') => {
    if (file.size > 10 * 1024 * 1024) {
      setError(`${target === 'logo' ? 'Logo' : 'Banner'} file size must be less than 10MB`);
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onloadend = () => {
      setRawCropSrc(reader.result as string);
      setCropTarget(target);
    };
    reader.readAsDataURL(file);
  };

  const handleDesignerCropComplete = (croppedBlob: Blob) => {
    // Logos are cropped to PNG so transparent backgrounds survive (JPEG turns them black)
    const fileName = cropTarget === 'logo' ? 'designer-logo.png' : 'designer-banner.jpg';
    const file = new File([croppedBlob], fileName, { type: croppedBlob.type });
    if (cropTarget === 'logo') {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(croppedBlob));
    } else if (cropTarget === 'banner') {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(croppedBlob));
    }
    setCropTarget(null);
    setRawCropSrc(null);
  };

  const handleDesignerCropCancel = () => {
    setCropTarget(null);
    setRawCropSrc(null);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) openCropForFile(file, 'logo');
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) openCropForFile(file, 'banner');
  };

  const handleDeleteLogo = async () => {
    if (designer && (designer.logoPath || designer.logoUrl)) {
      const confirmed = window.confirm(
        'This will permanently delete the logo from the server. This action cannot be undone. Are you sure you want to continue?'
      );
      
      if (!confirmed) return;
      
      setUploadingLogo(true);
      try {
        await designerService.deleteLogo(designer.id);
        setLogoPreview('');
        showToast({ type: 'success', title: 'Logo deleted successfully' });
      } catch (err) {
        showToast({ type: 'error', title: 'Failed to delete logo' });
      } finally {
        setUploadingLogo(false);
      }
    } else {
      setLogoFile(null);
      setLogoPreview('');
    }
  };

  const handleDeleteBanner = async () => {
    if (designer && (designer.bannerPath || designer.bannerUrl)) {
      const confirmed = window.confirm(
        'This will permanently delete the banner from the server. This action cannot be undone. Are you sure you want to continue?'
      );
      
      if (!confirmed) return;
      
      setUploadingBanner(true);
      try {
        await designerService.deleteBanner(designer.id);
        setBannerPreview('');
        showToast({ type: 'success', title: 'Banner deleted successfully' });
      } catch (err) {
        showToast({ type: 'error', title: 'Failed to delete banner' });
      } finally {
        setUploadingBanner(false);
      }
    } else {
      setBannerFile(null);
      setBannerPreview('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let savedDesigner: Designer;
      
      if (designer) {
        savedDesigner = await designerService.updateDesigner(designer.id, formData as UpdateDesignerRequest);
      } else {
        savedDesigner = await designerService.createDesigner(formData as CreateDesignerRequest);
      }
      
      // Upload images if new files were selected
      if (logoFile) {
        setUploadingLogo(true);
        try {
          await designerService.uploadLogo(savedDesigner.id, logoFile);
        } catch (err) {
          showToast({ type: 'error', title: 'Failed to upload logo' });
        } finally {
          setUploadingLogo(false);
        }
      }
      
      if (bannerFile) {
        setUploadingBanner(true);
        try {
          await designerService.uploadBanner(savedDesigner.id, bannerFile);
        } catch (err) {
          showToast({ type: 'error', title: 'Failed to upload banner' });
        } finally {
          setUploadingBanner(false);
        }
      }
      
      // Show success message
      showToast({ 
        type: 'success', 
        title: designer ? 'Designer updated successfully' : 'Designer created successfully' 
      });
      
      onSave();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save designer';
      // Make the duplicate name error clearer
      if (err.response?.status === 409 && errorMessage.includes('already exists')) {
        setError('A designer with this name already exists. Please choose a different name.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full p-6 my-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          {designer ? 'Edit Designer' : 'Add Designer'}
        </h2>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <input
                type="text"
                name="name"
                id="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows={4}
                value={formData.description || ''}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter a paragraph about the designer..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo
                </label>
                <div className="space-y-2">
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-48 object-contain border border-gray-300 rounded-md"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteLogo}
                        disabled={uploadingLogo}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded hover:bg-red-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => logoInputRef.current?.click()}
                      className="w-full h-48 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-gray-400"
                    >
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">Click to upload logo</p>
                      <p className="text-xs text-gray-500">Image will be cropped to 1:1 (1000×1000px)</p>
                    </div>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner
                </label>
                <div className="space-y-2">
                  {bannerPreview ? (
                    <div className="relative">
                      <img
                        src={bannerPreview}
                        alt="Banner preview"
                        className="w-full h-48 object-cover border border-gray-300 rounded-md"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteBanner}
                        disabled={uploadingBanner}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded hover:bg-red-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => bannerInputRef.current?.click()}
                      className="w-full h-48 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-gray-400"
                    >
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">Click to upload banner</p>
                      <p className="text-xs text-gray-500">Image will be cropped to 1920×810px</p>
                    </div>
                  )}
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBannerChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isActive"
                    value="true"
                    checked={formData.isActive === true}
                    onChange={() => setFormData(prev => ({ ...prev, isActive: true }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enabled</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isActive"
                    value="false"
                    checked={formData.isActive === false}
                    onChange={() => setFormData(prev => ({ ...prev, isActive: false }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Disabled</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                When disabled, all dresses from this designer will be hidden from Style Matcher
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>

      {cropTarget && rawCropSrc && (
        <ImageCropModal
          imageSrc={rawCropSrc}
          aspectRatio={cropTarget === 'logo' ? undefined : 1920 / 810}
          title={cropTarget === 'logo' ? 'Crop Logo' : 'Crop Banner'}
          outputWidth={cropTarget === 'logo' ? undefined : 1920}
          outputHeight={cropTarget === 'logo' ? undefined : 810}
          mimeType={cropTarget === 'logo' ? 'image/png' : 'image/jpeg'}
          onCropComplete={handleDesignerCropComplete}
          onCancel={handleDesignerCropCancel}
        />
      )}
    </div>
  );
};

export default DesignerModal;